import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import BRAND from "../../brand";
import {
  CheckCircle2, XCircle, Lightbulb, Award, Quote, BarChart3
} from "lucide-react";

export function FeedbackTab({ evaluation }) {
  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#14B8A6]";
    if (score >= 60) return "text-[#F59E0B]";
    return "text-red-500";
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths */}
        <Card className="premium-card" data-testid="strengths-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-[#0B1F3B] flex items-center gap-2">
              <div className="w-8 h-8 bg-[#14B8A6]/10 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-[#14B8A6]" />
              </div>
              {BRAND.terms.strengths}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2.5">
              {evaluation.strengths?.map((strength) => (
                <li key={`str-${strength.substring(0, 40)}`} className="flex items-start gap-3 p-3 bg-[#14B8A6]/5 rounded-xl border border-[#14B8A6]/10">
                  <CheckCircle2 className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 leading-relaxed">{strength}</span>
                </li>
              ))}
              {(!evaluation.strengths || evaluation.strengths.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No specific strengths identified</p>
              )}
            </ul>
          </CardContent>
        </Card>

        {/* Missed Opportunities */}
        <Card className="premium-card" data-testid="missed-opportunities-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-[#0B1F3B] flex items-center gap-2">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                <XCircle className="w-4 h-4 text-red-500" />
              </div>
              Missed Opportunities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {evaluation.missed_opportunities?.map((opp, idx) => (
                <li key={`opp-${opp.moment?.substring(0, 40)}`} className="p-3 bg-red-50/50 rounded-xl border-l-4 border-red-400" data-testid={`missed-opp-${idx}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <Quote className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 italic">"{opp.moment}"</p>
                  </div>
                  <p className="text-sm text-slate-700 pl-5.5 font-medium">
                    <span className="text-red-600">Should have:</span> {opp.what_to_do}
                  </p>
                </li>
              ))}
              {(!evaluation.missed_opportunities || evaluation.missed_opportunities.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No missed opportunities detected</p>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Coaching Suggestions */}
      <Card className="premium-card" data-testid="coaching-suggestions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#0B1F3B] flex items-center gap-2">
            <div className="w-8 h-8 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
            </div>
            {BRAND.terms.suggestions}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {evaluation.coaching_suggestions?.map((suggestion, idx) => (
              <div key={`sug-${suggestion.substring(0, 40)}`} className="p-4 bg-[#F59E0B]/5 rounded-xl border border-[#F59E0B]/10">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-[#F59E0B]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[#F59E0B]">{idx + 1}</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{suggestion}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Performer Examples */}
      {evaluation.top_performer_examples?.length > 0 && (
        <Card className="premium-card" data-testid="top-performer-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-[#0B1F3B] flex items-center gap-2">
              <div className="w-8 h-8 bg-[#2563EB]/10 rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 text-[#2563EB]" />
              </div>
              What a Top Performer Would Say
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {evaluation.top_performer_examples.map((example) => (
                <div key={`ex-${example.situation?.substring(0, 40)}`} className="p-4 bg-[#2563EB]/5 rounded-xl border border-[#2563EB]/10">
                  <p className="text-sm text-slate-500 mb-2">
                    <span className="font-semibold text-[#0B1F3B]">Situation:</span> {example.situation}
                  </p>
                  <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-[#2563EB]/10">
                    <Quote className="w-4 h-4 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#2563EB] font-medium leading-relaxed">"{example.better_response}"</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Behavioral Patterns */}
      <Card className="premium-card" data-testid="behavioral-patterns-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#0B1F3B] flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-slate-600" />
            </div>
            Behavioral Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { key: "jumped_to_product_early", label: "Jumped to Product Early", good: false },
              { key: "asked_enough_questions", label: "Asked Enough Questions", good: true },
              { key: "handled_objections_well", label: "Handled Objections Well", good: true },
              { key: "discovered_family_needs", label: "Discovered Family Needs", good: true },
              { key: "discovered_income_protection", label: "Discovered Income Protection", good: true },
              { key: "discovered_future_goals", label: "Discovered Future Goals", good: true },
            ].map(({ key, label, good }) => {
              const value = evaluation.behavioral_patterns?.[key];
              const isPositive = good ? value : !value;
              return (
                <div key={key} className={`p-3 rounded-xl ${isPositive ? 'bg-[#14B8A6]/5 border border-[#14B8A6]/10' : 'bg-red-50 border border-red-100'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {isPositive ? (
                      <CheckCircle2 className="w-4 h-4 text-[#14B8A6]" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-xs font-semibold ${isPositive ? 'text-[#14B8A6]' : 'text-red-500'}`}>
                      {value ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">{label}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
